import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

import { of, switchMap } from 'rxjs';
import { UsersStore } from '@fry/lib/users';
import { ListDefaults, TableService } from '@fry/lib/list';
import { OrganisationService } from '@fry/lib/organisations';
import { EASLinkAction } from '@fry/components/common/buttons/buttons.service';
import { InteractiveTableOptions } from '@fry/components/common/list/interactive-table/interactive-table.component';
import { SelectedItems } from '@fry/components/common/list/interactive-table/interactive-table.interface';
import { FieldType, FormField } from '@fry/lib/forms';
import { AsyncTasksWizardStore } from '@fry/async-tasks/lib';
import { SecurityService } from '@fry/lib/security';


@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    providers: [TableService],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: `
      .actions-footer {
        align-items: center;
        background-color: var(--rsr-color-primary-50);
        display: flex;
        gap: 2em;
        justify-content: space-between;
        margin: 1.32em 0;
        padding: 0.64em;
      }
     `

})
export class UserListComponent implements OnInit, OnDestroy {

  public actions: EASLinkAction[] = [{
    type: 'link',
    label: $localize `Create user`,
    condition: () => this.organisation.canCreateUsers(),
    link: of(['add']),
    color: 'primary',
  }];

  public tableComponentOptions: InteractiveTableOptions = {
    enableFilters: true,
    enableExport: true,
    enableColumns: true,
    enableSelection: false,
    enablePagination: true,
    itemSingleLabel: $localize `user`,
    itemPluralLabel: $localize `users`,
    tableTitle: $localize `Users`,
  };

  public selectedItems: SelectedItems = { total: 0 };

  constructor(
    private tableService: TableService,
    private usersStore: UsersStore,
    private organisation: OrganisationService,
    private asyncTasksWizard: AsyncTasksWizardStore,
    private router: Router,
    private security: SecurityService,
  ) { }

  private getExportFormFields(): FormField[] {
    return [
      {
        id: 'includeUserFields',
        type: FieldType.FIELD,
        title: $localize `Include profile fields`,
        initial: true,
        widget: { type: 'boolean' }
      },
      {
        id: 'includeRelations',
        type: FieldType.FIELD,
        title: $localize `Include relations`,
        initial: false,
        widget: { type: 'boolean' }
      }
    ];
  }

  ngOnInit() {
    const defaults: ListDefaults = {
      listType: 'table',
      engine: 'es',
      pageSize: 10,
      searchModel: {
        filter: { state: ['active'] },
        sort: { sort_on: 'lastName', sort_order: 'asc' },
      },
      fixedSearchModel: {
        filter: {},
        sort: {},
        select: ['fullname', 'email', 'state', 'roles', 'createdDate', 'lastLoginDate', 'view'],
        options: {
          includeUserFields: true,
          includeRelations: true,
          readableColumnId: '{{fullname}}',
          filterFormat: "v2"
        }
      },
      featuredFilters: ['text', 'state'],
      preserveState: true,
    };
    defaults.exportFormFields = this.getExportFormFields();
    this.security.hasPermission('users.clear').pipe(
      switchMap(has => {
        this.tableComponentOptions = { ...this.tableComponentOptions, enableSelection: has };
        return this.tableService.setupWith(this.usersStore, defaults, 'all-users');
      })
    ).subscribe();
  }

  public onSelectedItems(value: SelectedItems) {
    this.selectedItems = value;
  }

  doBulkAction() {
    if (this.selectedItems.total === 0) {
      throw new Error('Cannot bulk if empty');
    }

    const data = {
      itemDocType: 'user',
      data: {
        source: {
          ids: this.selectedItems.ids,
          searchCriteria: this.selectedItems.searchCriteria?.filter
        },
        params: {},
        fixedSearch: this.tableService.getFixedModel(),
        search: this.tableService.getSearchModel(),
      },
      origin: {
        path: this.router.url,
        title: $localize `Users`,
      }
    };
    const task = this.asyncTasksWizard.blank(data);
    this.asyncTasksWizard.create(task).subscribe(result => {
      this.router.navigate([`bulk-actions/${result.id}/wizard`]);
    });
  }

  ngOnDestroy() { }

}
